import React from "react";
import { useNavigate } from "react-router-dom";

import { CompanyCard } from "../../../common/u1/CompanyCard";
import { cn } from "../../../common/u1/cn";
import { ClockIcon, CheckIcon } from "../../../../assets/ui/icons";
import { PurchaseBadge } from "./PurchaseBadge";
import { usePurchasedCompany } from "./usePurchasedCompany";
import {
  purchaseState,
  purchaseNote,
  daysLeft,
  purchasedOn,
  IN_PROGRESS,
  OPEN,
  EXPIRED,
} from "./purchasedState";

/* One purchased business. The company lookup is TRANSCRIBED from purchased/PurchasedCompanyCard.jsx: a TREVEX id (TRX…) resolves through getCompany, anything... */
export const PurchasedCompanyRow = ({ row, status, token }) => {
  const navigate = useNavigate();
  const { company, isTrevex, displayName, statusLabel, profileKey } =
    usePurchasedCompany(row, token);

  const state = purchaseState(row, status);
  const on = purchasedOn(row);

  const note = purchaseNote(state, {
    statusLabel,
    purchasedOn: on,
    analystBuilt: !isTrevex,
  });

  const ACTIONS = {
    [IN_PROGRESS]: {
      label: "Track request",
      /* The design gives an outstanding report a way to be chased. The live
         screen offers none; this goes to the requests screen, which is where
         an order's progress is already listed. See F-021. */
      onAction: () => navigate("/dashboard/request"),
    },
    /* Transcribed: the live card's open action is a link to the public profile
       in a new tab, keyed by the TREVEX id or the WorldBox win. */
    [OPEN]: {
      label: "View profile",
      onAction: () => window.open(`/search/${profileKey}`, "_blank"),
    },
    [EXPIRED]: {
      label: "Renew access",
      /* Where the live screen's own "Start Purchasing" goes. See F-021. */
      onAction: () => {
        window.scrollTo(0, 0);
        navigate("/plans");
      },
    },
  };
  const action = ACTIONS[state];

  return (
    <div className="overflow-hidden rounded-xl border border-[var(--line)] bg-white">
      <CompanyCard
        company={company}
        badge={
          <PurchaseBadge
            state={state}
            daysLeft={state === OPEN ? daysLeft(row?.expireDate) ?? undefined : undefined}
          />
        }
        actionLabel={action.label}
        actionAriaLabel={`${action.label} for ${displayName}`}
        onAction={action.onAction}
        /* The card draws its own border; inside this shell it would be a
           second rule, so it is suppressed — as the Following row does. */
        className="rounded-none border-0"
      />

      {note && <PurchaseNote note={note} />}
    </div>
  );
};

const TONES = {
  progress: {
    strip: "bg-[var(--warning-soft)]",
    ink: "text-[var(--gold-deep)]",
    glyph: <ClockIcon size={15} className="shrink-0" />,
  },
  done: {
    strip: "bg-[var(--bg)]",
    ink: "text-[var(--ink-2)]",
    glyph: <CheckIcon stroke="var(--success)" />,
  },
  muted: {
    strip: "bg-[var(--bg)]",
    ink: "text-[var(--muted)]",
    glyph: <ClockIcon size={15} className="shrink-0" />,
  },
};

export const PurchaseNote = ({ note }) => {
  const tone = TONES[note?.tone] || TONES.done;
  return (
    <p
      className={cn(
        "m-0 flex items-center gap-2.5 border-t border-[var(--line)] px-4 py-3",
        "text-[13px] font-medium font-sans",
        tone.strip,
        tone.ink
      )}
    >
      <span className="flex shrink-0">{tone.glyph}</span>
      {note.text}
    </p>
  );
};

export default PurchasedCompanyRow;
